import { CustomListKey, HomeCareLeadId, ILocation, RegionId } from "@sp-crm/core";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
    HoveredMapEntity,
    MapMarkerId,
} from "components/community-search/community-map/types";
import { SearchResultsMap } from "components/map/search-results-map";
import { MapSearchResults, MapView } from "components/map/types";
import { usStateCentroid } from "components/map/us-state-centroids";
import { trpc } from "components/rpc/trpc";
import "maplibre-gl/dist/maplibre-gl.css";
import React, { useCallback, useMemo } from "react";
import { useCustomList } from "store/selectors/bridge";
import { useTenantSettings } from "store/selectors/settings";
import { ClientMapPinActions } from "./client-map-pin-actions";
import { ClientMapSearchParams, toGeoParamsInput } from "./geo-params";

const emptyMapView: MapView = {
    zoomLevel: null,
    bounds: null,
    viewportHeight: 0,
    viewportWidth: 0,
};

interface ClientMapSearchResultsMapProps {
    hoveredClient: null | HoveredMapEntity;
    searchParams: ClientMapSearchParams;
    regionId: RegionId;
    onExplicitBoundsChanged: (northWest: ILocation, southEast: ILocation) => void;
}

export const ClientMapSearchResultsMap: React.FC<
    ClientMapSearchResultsMapProps
> = props => {
    const { hoveredClient, searchParams, regionId, onExplicitBoundsChanged } = props;
    const [mapView, setMapView] = React.useState<MapView | null>(null);
    const { customList } = useCustomList(CustomListKey.HomeCareLeadStatus);
    const { settings } = useTenantSettings();
    const fallbackCenter = useMemo(
        () => usStateCentroid(settings.defaultState),
        [settings.defaultState],
    );

    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.homeCareLead.mapSearch.queryOptions({
            searchString: searchParams.searchString,
            searchType: searchParams.searchType,
            assignedUserId: searchParams.assignedUserId,
            statusListItemIds: searchParams.statusListItemIds,
            requireStartOfCare: searchParams.requireStartOfCare,
            excludePastEndOfCare: searchParams.excludePastEndOfCare,
            geoParams: toGeoParamsInput(searchParams.geoParams),
            regionId,
            mapView: mapView ?? emptyMapView,
        }),
        placeholderData: keepPreviousData,
        enabled: !!mapView && !!customList,
    });

    const searchResults: MapSearchResults = useMemo(() => {
        if (!query.data) {
            return null;
        }

        return {
            entityClusters: query.data.clusters.map(c => ({
                lat: c.lat,
                lng: c.lng,
                count: c.count,
            })),
            entityCoordinates: query.data.coordinates.map(coordinate => ({
                id: coordinate.id as MapMarkerId,
                lat: coordinate.lat,
                lng: coordinate.lng,
                name: coordinate.name,
                color: "violet",
                appLink: coordinate.appLink,
            })),
            searchCoordinates: query.data.searchCoordinates,
            recommendedZoom: query.data.recommendedZoom,
        };
    }, [query.data]);

    const statusListItemIdById = useMemo(
        () =>
            new Map(
                (query.data?.coordinates ?? []).map(coordinate => [
                    coordinate.id,
                    coordinate.statusListItemId,
                ]),
            ),
        [query.data],
    );

    const renderMarkerControl = useCallback(
        (id: MapMarkerId) => (
            <ClientMapPinActions
                leadId={id as HomeCareLeadId}
                statusListItemId={statusListItemIdById.get(id as HomeCareLeadId) ?? null}
            />
        ),
        [statusListItemIdById],
    );

    return (
        <SearchResultsMap
            explicitBounds={searchParams.geoParams?.bounds || null}
            fallbackCenter={fallbackCenter}
            geoSearchKey={JSON.stringify(searchParams.geoParams ?? null)}
            onExplicitBoundsChanged={onExplicitBoundsChanged}
            hoveredEntity={hoveredClient}
            isLoading={query.isLoading}
            basemapPolicy="selfHostedOnly"
            surface="client-map-view"
            onMapViewChanged={setMapView}
            results={searchResults}
            renderMarkerControl={renderMarkerControl}
            error={query.error}
        />
    );
};
